import { NavLink } from "react-router-dom";
import { Zap, LogOut, Search, X, ChevronDown, GripVertical } from "lucide-react";
import { BADGE_MAP, TONE_CLASS, ADMIN_SECTION } from "./navConfig";

export const Sidebar = ({
  brand,
  drawerOpen,
  onCloseDrawer,
  navQuery,
  setNavQuery,
  filteredSections,
  anyMatch,
  collapsed,
  toggleCollapse,
  badges,
  onLogout,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dragLabel,
  dropTarget,
}) => {
  return (
    <aside
      data-testid="sidebar"
      className={`sidebar-shell w-64 shrink-0 p-5 flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-out lg:static lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
    >
      {/* Brand */}
      <div className="flex items-center gap-2 mb-6 fade-in-up" data-testid="brand-logo">
        {brand.logo_url ? (
          <img
            src={brand.logo_url}
            alt="logo"
            className="w-9 h-9 rounded-xl object-cover bg-white brand-glow transition-transform duration-200 hover:scale-105"
          />
        ) : (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-black brand-glow transition-transform duration-200 hover:scale-105"
            style={{ background: brand.accent_color }}
          >
            <Zap size={20} strokeWidth={2.5} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold tracking-tight leading-none text-lg truncate">{brand.company_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{brand.tagline}</div>
        </div>
        <button
          type="button"
          onClick={onCloseDrawer}
          aria-label="Close menu"
          data-testid="drawer-close"
          className="lg:hidden text-slate-400 hover:text-slate-200 p-1 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
        <input
          value={navQuery}
          onChange={(e) => setNavQuery(e.target.value)}
          data-testid="nav-search"
          placeholder="Jump to…"
          className="nav-search-input w-full pl-9 pr-8 py-2 text-sm bg-[#0b0d12]/80 border border-[#2a3447] rounded-lg outline-none placeholder:text-slate-500 transition-all"
        />
        {navQuery && (
          <button
            type="button"
            onClick={() => setNavQuery("")}
            aria-label="Clear search"
            data-testid="nav-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="space-y-3 flex-1 overflow-y-auto sidebar-scroll -mx-1 px-1 pb-2" data-testid="sidebar-nav">
        {filteredSections.map((section, idx) => {
          const isAdminSection = section.label === ADMIN_SECTION.label;
          const isCollapsed = !navQuery && !!collapsed[section.label];
          const isDropTarget = dropTarget === section.label;
          const isDragging = dragLabel === section.label;
          return (
            <div
              key={section.label}
              data-testid={`nav-section-${section.label.toLowerCase()}`}
              style={{ animationDelay: `${idx * 40}ms` }}
              className={`space-y-0.5 rounded-lg transition-all fade-in-up ${
                isDropTarget ? "bg-[color:var(--accent)]/10 ring-1 ring-[color:var(--accent)]/40" : ""
              } ${isDragging ? "opacity-40 scale-[0.98]" : ""}`}
              onDragOver={onDragOver(section.label)}
              onDrop={onDrop(section.label)}
              onDragEnd={onDragEnd}
            >
              <div
                draggable={!navQuery && !isAdminSection}
                onDragStart={onDragStart(section.label)}
                className={`px-2 pt-1 pb-1 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold flex items-center gap-2 ${
                  !navQuery && !isAdminSection ? "cursor-grab active:cursor-grabbing" : ""
                } group`}
                data-testid={`nav-section-header-${section.label.toLowerCase()}`}
              >
                {!navQuery && !isAdminSection && (
                  <GripVertical size={11} className="opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                )}
                <button
                  type="button"
                  onClick={() => !navQuery && toggleCollapse(section.label)}
                  disabled={!!navQuery}
                  data-testid={`nav-toggle-${section.label.toLowerCase()}`}
                  className="flex items-center gap-1 hover:text-slate-200 transition-colors disabled:opacity-100 disabled:cursor-default"
                >
                  <ChevronDown size={12} className={`transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} />
                  <span>{section.label}</span>
                </button>
                <span className="flex-1 h-px bg-gradient-to-r from-[#2a3447]/70 to-transparent" />
              </div>
              {!isCollapsed &&
                section.items.map(({ to, label, icon: Icon, testid }) => {
                  const badgeCfg = BADGE_MAP[to];
                  const badgeCount = badgeCfg ? Number(badges[badgeCfg.key] || 0) : 0;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/"}
                      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                      data-testid={testid}
                    >
                      <Icon size={18} className="nav-link-icon" />
                      <span className="text-sm flex-1">{label}</span>
                      {badgeCount > 0 && (
                        <span
                          data-testid={`badge-${badgeCfg.key}`}
                          title={`${badgeCount} new`}
                          className={`ml-auto text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center leading-none ${TONE_CLASS[badgeCfg.tone] || TONE_CLASS.red}`}
                        >
                          {badgeCount > 9 ? "9+" : badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
            </div>
          );
        })}
        {!anyMatch && (
          <div className="text-xs text-slate-500 px-3 py-4 text-center" data-testid="nav-empty">
            No pages match “{navQuery}”
          </div>
        )}
      </nav>

      <button
        onClick={onLogout}
        data-testid="logout-button"
        className="mt-4 nav-link text-slate-400 hover:text-[#ff3b5c]"
      >
        <LogOut size={18} className="nav-link-icon" />
        <span className="text-sm">Sign out</span>
      </button>
    </aside>
  );
};

export default Sidebar;
